import { useCallback, useMemo, useState, ReactNode, ReactElement } from "react";
import {
  AddressFieldWithSearchConfig,
  isAddressFieldWithMelissaConfig,
} from "@evergiving/common/dist/es2015/form";
import {
  PostalAddressKind,
  LoqateResponseAddress,
  MelissaResponseAddress,
  LoqateResultType,
} from "@evergiving/common/dist/es2015/pledge";
import { useDispatch, useSelector } from "react-redux";
import { notNil } from "@evergiving/common/dist/es2015/typescriptUtils";

import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { AddressPill } from "@/lib/components/AddressPill";
import { Text } from "@/lib/components/Text";
import { Spinner } from "@/lib/components/Spinner";
import { setPostalAddress } from "@/lib/redux/actions";
import { FormattedNumber } from "@/lib/components/FormattedNumber";
import { RootState } from "@/lib/redux/types";
import { MelissaResponse } from "@/lib/misc/MelissaResponse";
import { LoqateResponse } from "@/lib/misc/LoqateResponse";

export const SearchAddressField = ({
  locale,
  addressFieldConfig,
  results,
  onChange,
  onSearch,
  onClear,
  loading,
  value,
  defaultCountryCode,
}: {
  locale: string;
  addressFieldConfig: AddressFieldWithSearchConfig;
  results: MelissaResponse | LoqateResponse | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onClear: () => void;
  loading: boolean;
  value: string;
  defaultCountryCode: string;
}): ReactElement => {
  const maxNumShown = 5;

  const pills: ReactNode[] = [];

  const isMelissaSearch = isAddressFieldWithMelissaConfig(addressFieldConfig);

  const {
    labelText,
    placeholderText,
    noAddressesFoundTemplateText,
    manualEntryButtonText,
  } = isMelissaSearch ? addressFieldConfig.melissa : addressFieldConfig.loqate;

  const isMelissaResponse = (
    searchResults: MelissaResponse | LoqateResponse
  ): searchResults is MelissaResponse =>
    (searchResults as MelissaResponse).Results !== undefined;

  const isMelissaResponseAddress = (
    responseAddress: MelissaResponseAddress | LoqateResponseAddress
  ): responseAddress is MelissaResponseAddress =>
    (responseAddress as MelissaResponseAddress).Address !== undefined;

  const areMelissaResponseAddresses = (
    responseAddresses: MelissaResponseAddress[] | LoqateResponseAddress[]
  ): responseAddresses is MelissaResponseAddress[] =>
    (responseAddresses as MelissaResponseAddress[]).filter(
      (address: MelissaResponseAddress) => isMelissaResponseAddress(address)
    ) && true;

  let addresses:
    | MelissaResponseAddress[]
    | LoqateResponseAddress[]
    | null = null;

  if (results && isMelissaResponse(results)) {
    addresses = results?.Results
      ? results.Results.map((result) => result.Address).filter(notNil)
      : null;
  } else {
    addresses = results?.Items
      ? results.Items.map((result) => result)
          .filter(notNil)
          .filter((result) => result.Type === LoqateResultType.Address)
      : null;
  }

  const numResults = addresses ? addresses.length : null;
  const numShown = Math.min(numResults || 0, maxNumShown);

  const shownAddresses:
    | MelissaResponseAddress[]
    | LoqateResponseAddress[] = useMemo(
    () => (addresses ? addresses.slice(0, numShown) : []),
    [addresses, numShown]
  );

  const [selectedAddress, setSelectedAddress] = useState<string>("");

  const selectedFullAddress:
    | MelissaResponseAddress
    | LoqateResponseAddress
    | undefined = areMelissaResponseAddresses(shownAddresses)
    ? shownAddresses.find(
        (address: MelissaResponseAddress) => address.Address === selectedAddress
      )
    : shownAddresses.find(
        (address: LoqateResponseAddress) => address.Text === selectedAddress
      );

  shownAddresses.forEach((address, index) => {
    pills.push(
      <AddressPill
        className="addressMatch"
        key={isMelissaResponseAddress(address) ? address.Address : address.Text}
        address={
          isMelissaResponseAddress(address)
            ? { kind: PostalAddressKind.Melissa, ...address }
            : { kind: PostalAddressKind.Loqate, ...address }
        }
        selected={selectedFullAddress === address}
        scrollIntoView={
          selectedFullAddress
            ? selectedFullAddress === address
            : index === shownAddresses.length - 1
        }
      >
        <div className="myAddressIs">
          <Text
            value={
              isMelissaSearch
                ? addressFieldConfig.melissa.addressSuggestionTemplateText
                : addressFieldConfig.loqate.addressSuggestionTemplateText
            }
            args={{
              suggestion: (
                <span>
                  {isMelissaResponseAddress(address)
                    ? address.Address
                    : address.Text}
                </span>
              ),
            }}
          />
        </div>
      </AddressPill>
    );
  });
  const dispatch = useDispatch();

  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const handleShowFreeFormAddress = useCallback(() => {
    dispatch(
      setPostalAddress({
        kind: PostalAddressKind.FreeForm,
        element2: currentCountryCode ?? defaultCountryCode,
        element3: undefined,
        element4: undefined,
        element5: undefined,
        element6: undefined,
        element7: undefined,
        element8: undefined,
        element9: undefined,
        element10: undefined,
        element11: undefined,
        element12: undefined,
        element13: undefined,
        element14: undefined,
        element15: undefined,
      })
    );
  }, [dispatch, currentCountryCode, defaultCountryCode]);

  const moveSelection = useCallback(
    (delta: number) => {
      if (shownAddresses) {
        const selectedIndex = shownAddresses.findIndex((address) =>
          isMelissaResponseAddress(address)
            ? address.Address === selectedAddress
            : address.Text === selectedAddress
        );

        const newSelectedIndex =
          (selectedIndex + shownAddresses.length + delta) %
          shownAddresses.length;

        if (addresses) {
          setSelectedAddress(
            areMelissaResponseAddresses(addresses)
              ? addresses[newSelectedIndex].Address
              : addresses[newSelectedIndex].Text
          );
        } else {
          setSelectedAddress("");
        }
      }
    },
    [shownAddresses, addresses, selectedAddress]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        moveSelection(1);
      } else if (event.key === "ArrowUp") {
        moveSelection(-1);
      } else if (event.key === "Escape") {
        onClear();
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (selectedFullAddress) {
          dispatch(
            setPostalAddress(
              isMelissaResponseAddress(selectedFullAddress)
                ? {
                    kind: PostalAddressKind.Melissa,
                    ...selectedFullAddress,
                  }
                : { kind: PostalAddressKind.Loqate, ...selectedFullAddress }
            )
          );
        } else {
          onSearch();
        }
      }
    },
    [moveSelection, dispatch, onClear, onSearch, selectedFullAddress]
  );

  const {
    tooManyAddressesFoundTemplateText,
    manyAddressesFoundTemplateText,
    someAddressesFoundTemplateText,
  } = isMelissaSearch ? addressFieldConfig.melissa : addressFieldConfig.loqate;

  return (
    <>
      <LabelledField id="billingAddress">
        <LabelText>{labelText}</LabelText>
        <div
          className="addressEntry"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded
          aria-owns="address-list"
          aria-controls="address-list"
        >
          <div className="spinner" aria-live="polite" role="status">
            <Spinner
              className={loading ? "spinnerVisible" : "spinnerInvisible"}
            />
            {loading && (
              <span className="screenReaderOnly">
                Loading address suggestions
              </span>
            )}
          </div>

          <input
            className="textField"
            type="text"
            inputMode="search"
            autoComplete="billing street-address"
            onKeyDown={handleKeyDown}
            onChange={onChange}
            placeholder={placeholderText}
            value={value}
            aria-autocomplete="list"
            aria-describedby="address-hint"
          />
        </div>
        <div className="addressHint" id="address-hint">
          {numResults === null && <></>}
          {numResults === 0 && (
            <Text
              value={noAddressesFoundTemplateText}
              args={{
                manualEntryButton: (
                  <button
                    type="button"
                    className="inlineButton"
                    onClick={handleShowFreeFormAddress}
                  >
                    {manualEntryButtonText}
                  </button>
                ),
              }}
            />
          )}
          {numResults !== null && numResults > 0 && (
            <Text
              value={
                // eslint-disable-next-line no-nested-ternary
                numResults > numShown
                  ? numResults >= maxNumShown
                    ? tooManyAddressesFoundTemplateText
                    : manyAddressesFoundTemplateText
                  : someAddressesFoundTemplateText
              }
              args={{
                numResults: (
                  <FormattedNumber locale={locale} value={numResults} />
                ),
                manualEntryButton: (
                  <button
                    type="button"
                    className="inlineButton"
                    onClick={handleShowFreeFormAddress}
                  >
                    {manualEntryButtonText}
                  </button>
                ),
              }}
            />
          )}
        </div>
        <ul role="listbox" id="address-list">
          {pills}
        </ul>
      </LabelledField>
    </>
  );
};
